import { useEffect, useMemo, useState } from "react";
import type { UiCommandBusV2, UiSnapshotV2 } from "@/ui/runtime/contracts/manager-ui-v2";
import { Avatar, AvatarImage } from "@/ui/foundation/primitives/avatar";
import { Badge } from "@/ui/foundation/primitives/badge";
import { Button } from "@/ui/foundation/primitives/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/ui/foundation/primitives/card";
import { DataList } from "@/ui/components/common/data-list";
import { InfoCallout } from "@/ui/components/common/info-display";
import { AttributeScoreBar } from "@/ui/components/common/attribute-score-bar";
import { ManagerSecondaryPane } from "@/ui/components/manager/manager-layout";
import { Tabs, TabsList, TabsTrigger } from "@/ui/foundation/primitives/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/ui/foundation/primitives/popover";
import { ChevronDownIcon } from "lucide-react";
import {
    avatarDataUri,
    formatMoney,
    formatTransferMarketValue,
    transferNextStep,
    transferStatusLabel,
    transferStatusVariant,
} from "@/ui/components/manager/manager-ui-helpers";

type TransferListMode = "buy" | "sell";

interface UseTransfersScreenStateResult {
    transferTargets: UiSnapshotV2["routes"]["transfers"]["targets"];
    listedPlayers: UiSnapshotV2["routes"]["transfers"]["listedPlayers"];
    transferNegotiations: UiSnapshotV2["routes"]["transfers"]["negotiations"];
    listMode: TransferListMode;
    selectedTarget: UiSnapshotV2["routes"]["transfers"]["targets"][number];
    selectedListedPlayer: UiSnapshotV2["routes"]["transfers"]["listedPlayers"][number] | null;
    selectedNegotiation: UiSnapshotV2["routes"]["transfers"]["negotiations"][number] | null;
    setListMode(mode: TransferListMode): void;
    selectTarget(targetId: string): void;
    selectListedPlayer(playerId: number): void;
    selectNegotiation(negotiationId: string): void;
}

export function useTransfersScreenState(snapshot: UiSnapshotV2): UseTransfersScreenStateResult {
    const transferTargets = snapshot.routes.transfers.targets;
    const listedPlayers = snapshot.routes.transfers.listedPlayers;
    const transferNegotiations = snapshot.routes.transfers.negotiations;
    const focusTargetId = snapshot.routes.transfers.focusTargetId ?? null;
    const focusVersion = snapshot.routes.transfers.focusVersion ?? 0;
    const [listMode, setListMode] = useState<TransferListMode>("buy");
    const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
    const [selectedListedPlayerId, setSelectedListedPlayerId] = useState<number | null>(null);
    const [selectedNegotiationId, setSelectedNegotiationId] = useState<string | null>(null);
    const [appliedFocusVersion, setAppliedFocusVersion] = useState<number>(0);

    useEffect(() => {
        if (!selectedTargetId && transferTargets[0]) {
            setSelectedTargetId(transferTargets[0].id);
        }
    }, [selectedTargetId, transferTargets]);

    useEffect(() => {
        if (!selectedNegotiationId && transferNegotiations[0]) {
            setSelectedNegotiationId(transferNegotiations[0].id);
        }
    }, [selectedNegotiationId, transferNegotiations]);

    useEffect(() => {
        if (!selectedListedPlayerId && listedPlayers[0]) {
            setSelectedListedPlayerId(listedPlayers[0].playerId);
        }
    }, [selectedListedPlayerId, listedPlayers]);

    useEffect(() => {
        if (!focusTargetId || focusVersion <= appliedFocusVersion) {
            return;
        }
        if (!transferTargets.some((target) => target.id === focusTargetId)) {
            return;
        }
        setSelectedTargetId(focusTargetId);
        setAppliedFocusVersion(focusVersion);
    }, [focusTargetId, focusVersion, appliedFocusVersion, transferTargets]);

    const selectedTarget = useMemo(
        () => transferTargets.find((target) => target.id === selectedTargetId) ?? transferTargets[0] ?? {
            id: "none",
            name: "No Target",
            position: "N/A",
            rating: 0,
            value: 0,
            status: "scouted",
        },
        [transferTargets, selectedTargetId]
    );

    const selectedNegotiation = useMemo(
        () => transferNegotiations.find((negotiation) => negotiation.id === selectedNegotiationId) ?? transferNegotiations[0] ?? null,
        [transferNegotiations, selectedNegotiationId]
    );

    const selectedListedPlayer = useMemo(
        () => listedPlayers.find((player) => player.playerId === selectedListedPlayerId) ?? listedPlayers[0] ?? null,
        [listedPlayers, selectedListedPlayerId]
    );

    return {
        transferTargets,
        listedPlayers,
        transferNegotiations,
        listMode,
        selectedTarget,
        selectedListedPlayer,
        selectedNegotiation,
        setListMode,
        selectTarget: setSelectedTargetId,
        selectListedPlayer: setSelectedListedPlayerId,
        selectNegotiation: setSelectedNegotiationId,
    };
}

interface TransfersScreenSecondaryProps {
    commands: UiCommandBusV2;
    state: UseTransfersScreenStateResult;
}

export function TransfersScreenSecondary({ state }: TransfersScreenSecondaryProps) {
    return (
        <ManagerSecondaryPane title="Transfers">
            <Tabs value={state.listMode} onValueChange={(value) => state.setListMode(value === "sell" ? "sell" : "buy")} className="flex min-h-0 flex-col gap-3 p-3">
                <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="buy">Buy</TabsTrigger>
                    <TabsTrigger value="sell">Sell</TabsTrigger>
                </TabsList>
                {state.listMode === "buy" ? (
                    <DataList
                        items={state.transferTargets}
                        getKey={(target) => target.id}
                        selectedKey={state.selectedTarget.id}
                        onSelect={(target) => {
                            state.selectTarget(target.id);
                        }}
                        itemClassName="flex items-center gap-3"
                        renderItem={(target) => (
                            <>
                                <Avatar className="size-9">
                                    <AvatarImage alt={target.name} src={avatarDataUri(target.avatarSvg)} />
                                </Avatar>
                                <div className="min-w-0 flex-1">
                                    <div className="truncate text-sm font-medium">{target.name}</div>
                                    <div className="text-xs text-muted-foreground">{target.position} · {target.clubName ?? "Unknown club"}</div>
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                    <div className="text-xs text-muted-foreground">{formatTransferMarketValue(target.value)}</div>
                                    <Badge variant="secondary" className="text-[10px]">
                                        {listingStatusLabel(target.listingStatus)}
                                    </Badge>
                                </div>
                            </>
                        )}
                    />
                ) : (
                    <DataList
                        items={state.listedPlayers}
                        getKey={(player) => String(player.playerId)}
                        selectedKey={state.selectedListedPlayer ? String(state.selectedListedPlayer.playerId) : undefined}
                        onSelect={(player) => {
                            state.selectListedPlayer(player.playerId);
                        }}
                        itemClassName="flex items-center gap-3"
                        emptyState="No players currently listed for transfer."
                        renderItem={(player) => (
                            <>
                                <Avatar className="size-9">
                                    <AvatarImage alt={player.name} src={avatarDataUri(player.avatarSvg)} />
                                </Avatar>
                                <div className="min-w-0 flex-1">
                                    <div className="truncate text-sm font-medium">{player.name}</div>
                                    <div className="text-xs text-muted-foreground">{player.position}</div>
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                    <div className="text-xs text-muted-foreground">{formatTransferMarketValue(player.value)}</div>
                                    <Badge variant="secondary" className="text-[10px]">
                                        Listed {formatMoney(player.listedTargetPrice)}
                                    </Badge>
                                </div>
                            </>
                        )}
                    />
                )}
            </Tabs>
        </ManagerSecondaryPane>
    );
}

interface TransfersScreenMainProps {
    commands: UiCommandBusV2;
    state: UseTransfersScreenStateResult;
}

const ACTIVE_NEGOTIATION_STATUSES = new Set([
    "awaiting_club_response",
    "club_accepted",
    "awaiting_player_response",
    "offer_countered",
    "deal_agreed",
]);

const TRANSFER_ATTRIBUTE_FIELDS: Array<{ key: keyof NonNullable<UiSnapshotV2["routes"]["transfers"]["targets"][number]["attributes"]>; label: string }> = [
    { key: "pace", label: "Pace" },
    { key: "ballControl", label: "Ball Control" },
    { key: "passing", label: "Passing" },
    { key: "tackling", label: "Tackling" },
    { key: "endurance", label: "Endurance" },
    { key: "morale", label: "Morale" },
    { key: "positioning", label: "Positioning" },
    { key: "shotPower", label: "Shot Power" },
    { key: "jumping", label: "Jumping" },
    { key: "reactions", label: "Reactions" },
    { key: "composure", label: "Composure" },
    { key: "goalkeeping", label: "Goalkeeping" }
];

function listingStatusLabel(status?: string): string {
    if (status === "market_listed") return "Market Listed";
    if (status === "club_listed") return "Club Listed";
    if (status === "player_requested") return "Player Requested Move";
    return "Not Listed";
}

function listingReasonLabel(reason?: string): string {
    if (reason === "club_clearout") return "Club clear-out";
    if (reason === "surplus_to_requirements") return "Surplus to requirements";
    if (reason === "financial_pressure") return "Financial pressure";
    if (reason === "player_requested_move") return "Player requested move";
    if (reason === "contract_running_down") return "Contract running down";
    if (reason === "strategic_sale") return "Strategic sale";
    return "No listing reason";
}

function listingStatusMeaning(status?: string): string {
    if (status === "market_listed") {
        return "Market Listed: broadly available now; opening talks is usually easier.";
    }
    if (status === "club_listed") {
        return "Club Listed: the club is open to sale, but can still be firm on price.";
    }
    if (status === "player_requested") {
        return "Player Requested Move: the player is agitating to leave, which can soften stance.";
    }
    return "Not Listed: not openly for sale; negotiations are typically tougher.";
}

export function TransfersScreenMain({ commands, state }: TransfersScreenMainProps) {
    const [pendingExpressInterestTargetId, setPendingExpressInterestTargetId] = useState<string | null>(null);

    if (state.listMode === "sell") {
        const selectedPlayer = state.selectedListedPlayer;
        if (!selectedPlayer) {
            return (
                <Card>
                    <CardHeader>
                        <CardDescription>Sell Players</CardDescription>
                        <CardTitle>No Listed Players</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <InfoCallout tone="primary">List players from Squad to manage outgoing sales here.</InfoCallout>
                    </CardContent>
                </Card>
            );
        }
        return (
            <div className="grid gap-4">
                <Card>
                    <CardHeader>
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <CardDescription>Listed Player</CardDescription>
                                <CardTitle>{selectedPlayer.name}</CardTitle>
                            </div>
                            <div className="flex flex-wrap items-center justify-end gap-2">
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" className="gap-1.5">
                                            Remove From Transfer List
                                            <ChevronDownIcon className="size-3.5" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent align="end" className="w-72">
                                        <div className="grid gap-3">
                                            <div className="text-sm font-medium">Remove transfer listing?</div>
                                            <div className="text-xs text-muted-foreground">
                                                {selectedPlayer.name} will no longer appear on the transfer market list.
                                            </div>
                                            <div className="flex justify-end gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => void commands.transferUnlistPlayer(selectedPlayer.playerId)}
                                                >
                                                    Confirm Remove
                                                </Button>
                                            </div>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-4">
                        <div className="flex flex-wrap gap-2">
                            <Badge>{selectedPlayer.position}</Badge>
                            <Badge variant="secondary">Transfer Listed</Badge>
                        </div>
                        <div className="flex flex-col gap-4">
                                <div className="grid gap-2 text-sm sm:grid-cols-2">
                                    <div className="rounded-md border px-3 py-2">
                                        <div className="text-xs text-muted-foreground">Market Value</div>
                                        <div className="font-medium">{formatTransferMarketValue(selectedPlayer.value)}</div>
                                    </div>
                                    <div className="rounded-md border px-3 py-2">
                                        <div className="text-xs text-muted-foreground">Listed Price</div>
                                        <div className="font-medium">{formatMoney(selectedPlayer.listedTargetPrice)}</div>
                                    </div>
                                    <div className="rounded-md border px-3 py-2">
                                        <div className="text-xs text-muted-foreground">Overall Rating</div>
                                        <div className="font-medium">{selectedPlayer.rating}</div>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between text-sm font-medium">
                                        <span>Player Attributes</span>
                                    </div>
                                    <div className="grid gap-2 md:grid-cols-3">
                                        {TRANSFER_ATTRIBUTE_FIELDS.map(({ key, label }) => {
                                            const value = Math.round(Number(selectedPlayer.attributes[key] ?? selectedPlayer.rating ?? 50));
                                            return (
                                                <AttributeScoreBar
                                                    key={`listed-player-attr-${key}`}
                                                    label={label}
                                                    value={value}
                                                    uncertainty={0}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                                <InfoCallout tone="primary">Monitor incoming bids in the Inbox. You can remove this player from the transfer list at any time.</InfoCallout>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const selectedTarget = state.selectedTarget;
    const activeNegotiations = state.transferNegotiations.filter((negotiation) => ACTIVE_NEGOTIATION_STATUSES.has(negotiation.status));
    const selectedTargetNegotiation = activeNegotiations.find((negotiation) => negotiation.targetId === selectedTarget.id) ?? null;

    const canScoutFurther = ["scouted", "shortlisted", "club_rejected", "personal_terms_failed", "withdrawn", "offer_countered"].includes(selectedTarget.status);
    const canExpressInterest = (
        ["scouted", "shortlisted", "club_rejected", "personal_terms_failed", "withdrawn"].includes(selectedTarget.status)
        && !selectedTargetNegotiation
    );
    const isExpressInterestPending = pendingExpressInterestTargetId === selectedTarget.id;
    const actionBlockReason = selectedTarget.status === "cooldown"
        ? `No deal actions available: ${selectedTarget.clubName ?? "Selling club"} is cooling relations after the cancelled deal (${selectedTarget.cooldownDaysRemaining ?? 0} day${(selectedTarget.cooldownDaysRemaining ?? 0) === 1 ? "" : "s"} remaining).`
        : selectedTarget.status === "scouting_in_progress"
            ? "No deal actions available: scouting is still in progress."
            : null;

    return (
        <div className="grid gap-4">
            <Card>
                <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <CardDescription>Target Profile</CardDescription>
                            <CardTitle>{selectedTarget.name}</CardTitle>
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-2">
                            {canScoutFurther ? (
                                <Button variant="outline" onClick={() => void commands.transferShortlist(selectedTarget.id)}>
                                    Scout Further
                                </Button>
                            ) : null}
                            {canExpressInterest ? (
                                <Button
                                    disabled={isExpressInterestPending}
                                    onClick={() => {
                                        if (isExpressInterestPending) {
                                            return;
                                        }
                                        setPendingExpressInterestTargetId(selectedTarget.id);
                                        void commands.transferExpressInterest(selectedTarget.id).finally(() => {
                                            setPendingExpressInterestTargetId((current) => current === selectedTarget.id ? null : current);
                                        });
                                    }}
                                >
                                    {isExpressInterestPending ? "Sending Interest..." : "Express Interest"}
                                </Button>
                            ) : null}
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                    <div className="flex flex-wrap gap-2">
                        <Badge>{selectedTarget.position}</Badge>
                        <Badge variant="secondary">{listingStatusLabel(selectedTarget.listingStatus)}</Badge>
                        <Badge variant="outline">{listingReasonLabel(selectedTarget.listingReason)}</Badge>
                        {selectedTarget.status !== "scouted" ? (
                            <Badge variant={transferStatusVariant(selectedTarget.status)}>{transferStatusLabel(selectedTarget.status)}</Badge>
                        ) : null}
                    </div>
                    <div className="flex flex-col gap-4">
                            <InfoCallout tone="primary">{listingStatusMeaning(selectedTarget.listingStatus)}</InfoCallout>
                            {Math.round(Number(selectedTarget.scoutConfidence ?? 50)) < 45 ? (
                                <InfoCallout tone="primary">
                                    Limited report: this listed player is visible, but detailed scouting confidence is still low.
                                </InfoCallout>
                            ) : null}
                            <div className="grid gap-2 text-sm sm:grid-cols-2">
                                <div className="rounded-md border px-3 py-2">
                                    <div className="text-xs text-muted-foreground">Market Value</div>
                                    <div className="font-medium">{formatTransferMarketValue(selectedTarget.value)}</div>
                                </div>
                                <div className="rounded-md border px-3 py-2">
                                    <div className="text-xs text-muted-foreground">Requested Wage</div>
                                    <div className="font-medium">{formatMoney(selectedTarget.wageExpectation ?? 0)}/wk</div>
                                </div>
                                <div className="rounded-md border px-3 py-2">
                                    <div className="text-xs text-muted-foreground">Overall Rating</div>
                                    <div className="font-medium">{selectedTarget.rating}</div>
                                </div>
                                <div className="rounded-md border px-3 py-2">
                                    <div className="text-xs text-muted-foreground">Scout Confidence</div>
                                    <div className="font-medium">{selectedTarget.scoutConfidence ?? 50}</div>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <div className="flex items-center justify-between text-sm font-medium">
                                    <span>Scouting Report</span>
                                    <span className="text-xs text-muted-foreground">Scout uncertainty shown as ± points per attribute.</span>
                                </div>
                                {Math.round(Number(selectedTarget.scoutConfidence ?? 50)) < 45 ? (
                                    <div className="rounded-md border px-3 py-2 text-xs text-muted-foreground">
                                        Full attribute report unavailable at current confidence. Use Scout Further to improve certainty.
                                    </div>
                                ) : (
                                    <div className="grid gap-2 md:grid-cols-3">
                                        {TRANSFER_ATTRIBUTE_FIELDS.map(({ key, label }) => {
                                            const value = Math.round(Number(selectedTarget.attributes?.[key] ?? selectedTarget.rating ?? 50));
                                            const uncertainty = Math.max(0, Math.round(Number(selectedTarget.scoutUncertaintyByAttribute?.[key] ?? 0)));
                                            return (
                                                <AttributeScoreBar
                                                    key={`transfer-attr-${key}`}
                                                    label={label}
                                                    value={value}
                                                    uncertainty={uncertainty}
                                                />
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                            <InfoCallout tone="primary">{transferNextStep(selectedTarget.status)}</InfoCallout>
                            {actionBlockReason ? <div className="text-xs text-muted-foreground">{actionBlockReason}</div> : null}
                            {selectedTargetNegotiation ? (
                                <>
                                    <div className="flex items-center justify-between rounded-md border px-3 py-2">
                                        <span className="text-muted-foreground">Current Fee</span>
                                        <span className="font-medium">{formatMoney(selectedTargetNegotiation.bidAmount)}</span>
                                    </div>
                                    <div className="flex items-center justify-between rounded-md border px-3 py-2">
                                        <span className="text-muted-foreground">Current Wage</span>
                                        <span className="font-medium">{formatMoney(selectedTargetNegotiation.wageOffer)}/wk</span>
                                    </div>
                                    <div className="text-xs text-muted-foreground">{selectedTargetNegotiation.responseMessage}</div>
                                </>
                            ) : null}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
